import { Injectable, InjectionToken, inject } from '@angular/core';

import { Deferred } from '@frontend/sports/common/core/utils/deferred';
import { ApiBase, ApiServiceFactory, ClientConfigProductName, ProductService } from '@frontend/vanilla/core';
import { Observable } from 'rxjs';

export class Api extends ApiBase {}

export const API = new InjectionToken<Api>('sports-api', {
    providedIn: 'root',
    factory: () => {
        const factory = inject(ApiServiceFactory);
        const productService = inject(ProductService);
        const forwardProductApi = productService.getMetadata(ClientConfigProductName.SPORTS).isEnabled;

        return apiServiceFactory(factory, forwardProductApi);
    },
});

export const API_EXT = new InjectionToken<Api>('sports-api-ext', {
    providedIn: 'root',
    factory: () => {
        const factory = inject(ApiServiceFactory);

        return apiServiceFactory(factory, false);
    },
});

function apiServiceFactory(factory: ApiServiceFactory, forwardProductApi: boolean): Api {
    return factory.createForProduct(Api, {
        product: ClientConfigProductName.SPORTS,
        area: 'sports',
        forwardProductApiRequestHeader: forwardProductApi,
    });
}

abstract class ApiServiceBase {
    protected abstract readonly api: Api;

    get<T>(
        url: string,
        params?: unknown,
        options?: {
            showSpinner?: boolean;
            withCredentials?: boolean;
            cancelationToken?: Deferred<void>;
            retryCount?: number;
            retryDelay?: number;
            retryOnStatus?: number[];
            headers?: { [key: string]: string };
            responseType?: 'json' | 'text';
            resolveWithFullResponse?: boolean;
            transferCache?: boolean;
        },
    ): Observable<T> {
        const resultOptions = options ?? {
            showSpinner: false,
            withCredentials: true,
        };
        resultOptions.withCredentials = resultOptions.withCredentials === false ? false : true;

        return this.api.get(url, params || {}, {
            showSpinner: resultOptions.showSpinner,
            withCredentials: resultOptions.withCredentials,
            retryCount: resultOptions.retryCount,
            retryDelay: resultOptions.retryDelay,
            retryOnStatus: resultOptions.retryOnStatus,
            headers: resultOptions.headers,
            responseType: resultOptions.responseType,
            resolveWithFullResponse: resultOptions.resolveWithFullResponse,
            transferCache: resultOptions.transferCache,
        });
    }

    post<T>(
        url: string,
        data?: unknown,
        options?: {
            showSpinner?: boolean;
            withCredentials?: boolean;
            cancelationToken?: Deferred<void>;
            cache?: boolean;
            retryCount?: number;
            retryDelay?: number;
            headers?: { [key: string]: string };
            responseType?: 'json' | 'text';
            resolveWithFullResponse?: boolean;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params?: { [key: string]: any };
            transferCache?: boolean;
        },
    ): Observable<T> {
        let headers = {};

        const resultOptions = options ?? {
            showSpinner: false,
            resolveWithFullResponse: false,
            transferCache: false,
        };

        if (!resultOptions.cache) {
            headers = { 'cache-control': 'no-cache' };
        }

        headers = { ...headers, ...resultOptions.headers };

        const withCredentialsOptions = resultOptions.withCredentials === undefined ? {} : { withCredentials: resultOptions.withCredentials };

        return this.api.post(url, data, {
            headers,
            showSpinner: resultOptions.showSpinner,
            retryCount: resultOptions.retryCount,
            retryDelay: resultOptions.retryDelay,
            responseType: resultOptions.responseType,
            resolveWithFullResponse: resultOptions.resolveWithFullResponse,
            params: resultOptions.params,
            transferCache: resultOptions.transferCache,
            ...withCredentialsOptions,
        });
    }
}

@Injectable({ providedIn: 'root' })
export class ApiServiceExternal extends ApiServiceBase {
    readonly api = inject(API_EXT);
}

@Injectable({ providedIn: 'root' })
export class ApiService extends ApiServiceBase {
    readonly api = inject(API);
}
